<script setup>
import { ref, computed, watch } from "vue"
import { useQuery, useMutation } from "@vue/apollo-composable"
import { createToaster } from "@meforma/vue-toaster"
import { DateTime } from "luxon"
import { useRouter } from "vue-router"
import ME_QUERY from "@/graphql/User/MeQuery.gql"
import UPDATE_USER_MUTATION from "@/graphql/User/UpdateUserMutation.gql"
import COUNTRIES_QUERY from "@/graphql/Country/CountriesQuery.gql"
import Banner from "@/components/common/Banner.vue"
import PasswordUpdateModal from "@/components/common/PasswordUpdateModal.vue"
import RedirectParams from "./RedirectParams.vue"
import GlobalRules from "./GlobalRules.vue"

//initial values & watch for input changes
const router = useRouter()
const alertMessageProfile = ref(null)
const alertTypeProfile = ref(null)
const alertSection = ref(null)
const termsAgreed = ref(false)
const initialMe = ref({})
const profileHasChanges = ref(false)
const contactHasChanges = ref(false)
const taxFieldHasChanges = ref(false)
const toaster = createToaster({
  position: "bottom-right",
  duration: 4000,
})

//fields
const agreedToTermsBy = ref(null)
const agreedToTermsByPosition = ref(null)

// Password modal state
const showPasswordModal = ref(false)

const me = ref({
  loginEmail: null,
  disableEngagementEmails: null,
  profile: {
    firstName: null,
    lastName: null,
    contactEmail: null,
    messengerApp: null,
    messengerHandle: null,
    companyName: null,
  },
  contact: {
    country: null,
    address: null,
    city: null,
    region: null,
    zipPostal: null,
  },
  taxFields: {
    taxType: null,
    taxNumber: null,
  },
})

//user update mutation
const { mutate: updateUser, loading: isLoadingUserUpdate } =
  useMutation(UPDATE_USER_MUTATION)

// Password modal handlers
const openPasswordModal = () => {
  showPasswordModal.value = true
}

const handlePasswordSuccess = () => {
  toaster.show("Password updated successfully!", { type: "success" })
}

const openCustomTerms = () => {
  window.open(customTermsUrl.value, '_blank')
}

// Computed properties
const agreedDate = computed(() => {
  if (!meResult.value?.me?.userDetails?.agreedToTermsAt) return null
  return DateTime.fromFormat(
    meResult.value.me.userDetails.agreedToTermsAt,
    "yyyy-MM-dd HH:mm:ss",
  ).toFormat("LLLL d, yyyy")
})

const customTermsUrl = computed(() => {
  return meResult.value?.me?.userDetails?.customTermsUrl || null
})

const handleProfileUpdate = type => {
  updateUser({
    input: {
      id: meResult.value.me.id,
      contacts: {
        update: [
          {
            id: meResult.value.me.primaryContact.id,
            name:
              me.value.profile.firstName + " " + me.value.profile.lastName ??
              undefined,
            firstName: me.value.profile.firstName ?? undefined,
            lastName: me.value.profile.lastName ?? undefined,
            email: me.value.profile.contactEmail ?? undefined,
            company: me.value.profile.companyName ?? undefined,
            countryCode: me.value.contact.country?.code ?? undefined,
            address: me.value.contact.address ?? undefined,
            city: me.value.contact.city ?? undefined,
            region: me.value.contact.region ?? undefined,
            postalCode: me.value.contact.zipPostal ?? undefined,
            messenger: JSON.stringify({
              handle: me.value.profile.messengerHandle ?? undefined,
              app: me.value.profile.messengerApp ?? undefined,
            }),
            taxType: me.value.taxFields.taxType ?? undefined,
            taxNumber: me.value.taxFields.taxNumber ?? undefined,
          },
        ],
      },
    },
  })
    .then(() => {
      profileHasChanges.value = false
      contactHasChanges.value = false
      taxFieldHasChanges.value = false

      alertMessageProfile.value = "Profile Updated Succesfully"
      alertTypeProfile.value = "success"
      alertSection.value = type
    })
    .catch(error => {
      console.log(error)
      profileHasChanges.value = false
      contactHasChanges.value = false
      taxFieldHasChanges.value = false
      toaster.show("Something went wrong.", { type: "error" })
    })
}

const handleDisableEngagementEmails = () => {
  updateUser({
    input: {
      id: meResult.value.me.id,
      userDetails: {
        update: {
          id: meResult.value.me.userDetails.id,
          disableEngagementEmails: !me.value.disableEngagementEmails,
        },
      },
    },
  })
    .then(() => {
      toaster.show("Emails updated successfully.", { type: "success" })
    })
    .catch(error => {
      console.log(error)
      toaster.show("Something went wrong.", { type: "error" })
    })
}


watch(
  () => me.value,
  (newVal, oldVal) => {
    alertSection.value = null
    if (
      JSON.stringify(newVal.profile) !== JSON.stringify(initialMe.value.profile)
    ) {
      profileHasChanges.value = true
      alertMessageProfile.value = null
      alertTypeProfile.value = null
    }
    if (
      JSON.stringify(newVal.contact) !== JSON.stringify(initialMe.value.contact)
    ) {
      contactHasChanges.value = true
      alertMessageProfile.value = null
      alertTypeProfile.value = null
    }
    if (
      JSON.stringify(newVal.taxFields) !==
      JSON.stringify(initialMe.value.taxFields)
    ) {
      taxFieldHasChanges.value = true
      alertMessageProfile.value = null
      alertTypeProfile.value = null
    }
  },
  { deep: true },
)

//me query
const { result: meResult, loading: meLoading, onResult } = useQuery(ME_QUERY)
onResult(() => {
  if (meResult?.value) {
    const user = meResult.value.me
    me.value.id = user.id
    me.value.loginEmail = user.email.replace("allIn_", "")
    me.value.disableEngagementEmails = user.userDetails.disableEngagementEmails
    me.value.profile.firstName = user.primaryContact?.firstName
    me.value.profile.lastName = user.primaryContact?.lastName
    me.value.profile.contactEmail = user.contactEmail
    me.value.profile.companyName = user.primaryContact?.company
    me.value.contact.country = {
      label: user.primaryContact?.country?.name,
    }
    me.value.contact.address = user.primaryContact?.address
    me.value.contact.city = user.primaryContact?.city
    me.value.contact.region = user.primaryContact?.region
    me.value.contact.zipPostal = user.primaryContact?.postalCode
    me.value.taxFields.taxType = user.primaryContact?.taxType
    me.value.taxFields.taxNumber = user.primaryContact?.taxNumber

    const messenger = JSON.parse(user.primaryContact?.messenger || "{}")
    me.value.profile.messengerApp = messenger.app
    me.value.profile.messengerHandle = messenger.handle

    initialMe.value = JSON.parse(JSON.stringify(me.value))
    termsAgreed.value = user.userDetails.agreedToTermsAt ?? false

    const terms = JSON.parse(user.userDetails.agreedToTermsBy || "{}")
    agreedToTermsBy.value = terms.agreedBy
    agreedToTermsByPosition.value = terms.agreedByPosition

    // Redirect to Terms page if terms haven't been accepted
    if (!termsAgreed.value) {
      router.push({ name: 'Terms' })
    }
  }
})

const {
  result: countries,
  loading: countryLoading,
  error: countryError,
  onResult: onCountryResult,
} = useQuery(COUNTRIES_QUERY)
</script>

<template>
  <div v-show="meLoading || countryLoading">
    <v-icon name="ri-loader-5-line" class="ov-spin" />
  </div>
  <div v-if="!meLoading && !countryLoading" class="max-w-screen-lg divide-y divide-base-content/10 dark:divide-base-400 [&>*]:py-12">
    <div v-if="!termsAgreed" class="max-w-6xl grid grid-cols-1 md:grid-cols-10">
      <div class="md:col-span-2"></div>
      <!-- <Banner
        bannerClass="col-span-1 md:col-span-8 bg-gradient-to-r from-accent to-accent/80 border border-accent bg-clip-text text-transparent"
      > -->
      <Banner
        icon="bi-info-circle"
        iconClass="text-accent mr-2"
        bannerClass="text-sm col-span-1 md:col-span-8 bg-gradient-to-r from-accent to-accent/80 bg-clip-text text-transparent"
      >
        <div class="flex justify-between items-center">
          <div>
            <h6>
              You must accept
              <span class="font-bold"
                >YTZ Terms & Conditions</span
              >
              before viewing campaigns. You will be redirected to the dashboard
              upon acceptance of terms.
            </h6>
          </div>
        </div>
      </Banner>
    </div>
    <!-- profile -->
    <div
      v-if="termsAgreed"
      class="max-w-6xl grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-10"
    >
      <div class="md:col-span-2 flex flex-col">
        <h2 class="text-base/7 font-semibold">Profile</h2>
      </div>

      <div class="md:col-span-8">
        <!-- profile basics -->
        <form @submit.prevent="handleProfileUpdate('profile')">
          <div class="grid grid-cols-1 gap-y-6">
            <XInput
              v-model="me.loginEmail"
              id="email"
              label="Login Email"
              type="email"
              :readonly="true"
            />
            <div>
              <XInput
                v-model="me.profile.companyName"
                id="company-name"
                label="Company Name"
                type="text"
              />
            </div>
            <div class="grid grid-cols-2 gap-6">
              <XInput
                v-model="me.profile.firstName"
                id="first-name"
                label="First Name"
                type="text"
                required="true"
                requiredIconHidden="true"
              />
              <XInput
                v-model="me.profile.lastName"
                id="last-name"
                label="Last Name"
                type="text"
                required="true"
                requiredIconHidden="true"
              />
            </div>
            <XInput
              v-model="me.profile.contactEmail"
              id="email"
              label="Contact Email"
              type="email"
              required="true"
              requiredIconHidden="true"
            />
            <div class="grid grid-cols-2 lg:grid-cols-3 gap-6">
              <div class="col-span-1">
                <MultiSelect
                  v-model="me.profile.messengerApp"
                  inputLabel="App"
                  placeholder="Messenger App"
                  :options="['Teams', 'Telegram']"
                />
              </div>
              <div class="col-span-1 lg:col-span-2">
                <XInput
                  v-model="me.profile.messengerHandle"
                  id="messenger-handle"
                  label="Messenger ID"
                  type="text"
                  required="true"
                  requiredIconHidden="true"
                />
              </div>
            </div>
            <div
              v-if="profileHasChanges && !alertMessageProfile"
              class="flex -mt-2"
            >
              <XButton
                color="primary"
                type="submit"
                :isLoading="isLoadingUserUpdate"
                >Save Profile Changes</XButton>
            </div>
            <Alert
              v-if="alertSection == 'profile'"
              class="mt-2"
              :isShowing="alertMessageProfile"
              :alertMessage="alertMessageProfile"
              :type="alertTypeProfile"
            />
          </div>
        </form>
      </div>
    </div>

    <!-- Update Password -->
    <div
      v-if="termsAgreed"
      class="max-w-6xl grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-10"
    >
      <div class="md:col-span-2 flex flex-col">
        <h2 class="text-base/7 font-semibold">Update Password</h2>
      </div>

      <div class="md:col-span-8">
        <XButton
          type="button"
          color="primary"
          @click="openPasswordModal"
        >
          Change Password
        </XButton>
      </div>
    </div>

    <!-- Contact details -->
    <div
      v-if="termsAgreed"
      class="max-w-6xl grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-10"
    >
      <div class="md:col-span-2 flex flex-col">
        <h2 class="text-base/7 font-semibold">Address</h2>
      </div>
      <form
        @submit.prevent="handleProfileUpdate('address')"
        class="max-w-3xl grid grid-cols-1 md:grid-cols-7 gap-x-12 gap-y-8 md:col-span-8"
      >
        <div class="md:col-start-1 md:col-span-3">
          <MultiSelect
            v-model="me.contact.country"
            inputLabel="Country"
            placeholder="Country"
            :options="
              countries?.countries.filter(
                country => !['?', 'A1', 'A2', 'AP'].includes(country.code),
              ) || []
            "
            trackBy="label"
            label="label"
            :error="validationErrors?.includes('country')"
          />
        </div>

        <div class="md:col-span-5">
          <XInput
            v-model="me.contact.address"
            id="primary-address"
            label="Street Address"
            type="text"
          />
        </div>

        <div class="md:col-span-5">
          <div class="grid grid-cols-2 gap-6">
            <div class="col-span-1">
              <XInput
                v-model="me.contact.city"
                id="city"
                label="City"
                type="text"
                required="true"
                requiredIconHidden="true"
              />
            </div>
            <div class="col-span-1">
              <XInput
                v-model="me.contact.region"
                id="region"
                label="Region"
                type="text"
                required="true"
                requiredIconHidden="true"
              />
            </div>
            <div class="col-span-1">
              <XInput
                v-model="me.contact.zipPostal"
                id="zipPostal"
                label="ZIP / Postal"
                type="text"
                required="true"
                requiredIconHidden="true"
              />
            </div>
          </div>
        </div>

        <div
          v-if="contactHasChanges && !alertMessageProfile"
          class="md:col-span-5 -mt-4"
        >
          <XButton color="accent" class="justify-center" type="submit"
            >Save Address</XButton>
        </div>
        <div v-if="alertSection == 'address'" class="md:col-span-5">
          <Alert
            class="mt-2"
            :isShowing="alertMessageProfile"
            :alertMessage="alertMessageProfile"
            :type="alertTypeProfile"
          />
        </div>
      </form>
    </div>

    <!-- Finance details -->
    <div
      v-if="termsAgreed && me.taxFields.taxNumber"
      class="max-w-6xl grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-10"
    >
      <div class="md:col-span-2 flex flex-col">
        <h2 class="text-base/7 font-semibold">Financial Details</h2>
      </div>
      <form
        @submit.prevent="handleProfileUpdate('tax')"
        class="max-w-3xl grid grid-cols-1 md:grid-cols-7 gap-x-12 gap-y-8 md:col-span-8"
      >
        <div class="md:col-span-4">
          <MultiSelect
            v-model="me.taxFields.taxType"
            inputLabel="Tax Type"
            placeholder="Tax Type"
            :options="['HST', 'GST']"
            trackBy="label"
            label="label"
          />
        </div>

        <div class="md:col-span-4">
          <XInput
            v-model="me.taxFields.taxNumber"
            id="Tax Number"
            label="Tax Number"
            type="text"
          />
        </div>

        <div
          v-if="taxFieldHasChanges && !alertMessageProfile"
          class="md:col-span-5 -mt-4"
        >
          <XButton color="accent" class="justify-center" type="submit"
            >Save Financial Details</XButton>
        </div>
        <div class="md:col-span-5">
          <Alert
            v-if="alertSection == 'tax'"
            class="mt-2"
            :isShowing="alertMessageProfile"
            :alertMessage="alertMessageProfile"
            :type="alertTypeProfile"
          />
        </div>
      </form>
    </div>

    <!-- Notifications details -->
    <div
      v-if="termsAgreed"
      class="max-w-6xl grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-10"
    >
      <div class="md:col-span-2">
        <h2 class="text-base/7 font-semibold">Notifications</h2>
      </div>

      <div
        class="max-w-3xl md:col-span-8 grid grid-cols-1 md:grid-cols-7 gap-x-12 gap-y-8"
      >
        <div class="col-span-1">
          <input
            id="disableEngagementEmailsToggle"
            type="checkbox"
            class="toggle toggle-accent"
            :checked="!me.disableEngagementEmails"
            @change="handleDisableEngagementEmails"
          />
        </div>
        <div class="col-span-6 text-sm">
          <p>Receive helpful reminders about your account.</p>
          <p>
            Unsubscribing from reminders will not impact critical system wide
            updates or campaign updates.
          </p>
        </div>
      </div>
    </div>

    <!-- Redirect Params -->
    <div
      v-if="termsAgreed"
      class="max-w-6xl grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-10"
    >
      <div class="md:col-span-3">
        <h2 class="text-base/7 font-semibold">Preset Tracking Tokens</h2>
      </div>

      <div class="max-w-3xl md:col-span-7">
        <RedirectParams v-if="meResult" :user="meResult?.me || {}" />
      </div>
    </div>

    <!-- Global Rules -->
    <div
      v-if="termsAgreed"
      class="max-w-6xl grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-10"
    >
      <div class="md:col-span-3">
        <h2 class="text-base/7 font-semibold">Global Rules</h2>
        <p class="text-sm text-base-content/60 mt-1">
          Account-wide source restrictions
        </p>
      </div>

      <div class="max-w-3xl md:col-span-7">
        <GlobalRules :keywordBlocks="meResult?.me?.keywordBlocks || []" />
      </div>
    </div>

    <!-- Terms -->
    <div
      id="terms"
      class="max-w-6xl grid grid-cols-1 gap-x-8 gap-y-10 border-b border-neutral-content/20 md:grid-cols-10"
    >
      <div class="md:col-span-2">
        <h2 class="text-base/7 font-semibold">Terms</h2>
      </div>

      <div class="grid grid-cols-1 gap-x-12 gap-y-8 md:col-span-8">
        <div class="col-span-1 space-y-4">
          <Banner
            v-if="termsAgreed"
            variant="info"
            icon="bi-check-lg"
            bannerClass="text-sm"
          >
            <span v-if="agreedToTermsBy && me.profile.companyName">
              Terms accepted by {{ agreedToTermsBy }} ({{ agreedToTermsByPosition }}) at {{ me.profile.companyName }} on {{ agreedDate }}.
            </span>
            <span v-else-if="agreedToTermsBy">
              Terms accepted by {{ agreedToTermsBy }} on {{ agreedDate }}.
            </span>
            <span v-else>
              Terms accepted on {{ agreedDate }}.
            </span>
          </Banner>

          <XButton
            v-if="customTermsUrl"
            type="button"
            color="primary"
            @click="openCustomTerms"
          >
            View Terms PDF
          </XButton>
          <XButton
            v-else
            type="button"
            color="primary"
            @click="$router.push({ name: 'Terms' })"
          >
            View Terms & Conditions
          </XButton>
        </div>
      </div>
    </div>
  </div>

  <!-- Password Update Modal -->
  <PasswordUpdateModal
    v-model:isOpen="showPasswordModal"
    :userId="meResult?.me?.id"
    @success="handlePasswordSuccess"
  />
</template>

<style scoped>
.collapse {
  border-radius: 0;
}
.collapse-content {
  @apply peer-checked:bg-base-100/40 peer-checked:dark:bg-neutral-content/10 peer-checked:border-neutral-content/20 peer-checked:border-b peer-checked:border-l peer-checked:border-r rounded-b-lg;
}
.collapse-title {
  @apply peer-checked:bg-base-100/40  peer-checked:dark:bg-neutral-content/10 peer-checked:border-neutral-content/20 peer-checked:border-t peer-checked:border-l peer-checked:border-r rounded-t-lg;
  padding: 0.6rem;
  min-height: initial;
}
.collapse-title,
:where(.collapse > input[type="checkbox"]),
:where(.collapse > input[type="radio"]) {
  min-height: initial;
}
.collapse-arrow > .collapse-title:after {
  @apply text-neutral-content/30;
  top: 1.3rem;
}
</style>
